import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaBuilding,
  FaMapMarkerAlt,
  FaCity,
  FaIdCard,
  FaEnvelope,
  FaPhoneAlt,
  FaLayerGroup,
  FaBriefcase,
  FaSortNumericUp,
  FaInfoCircle,
  FaMoneyBillWaveAlt,
  FaCalendarMinus,
  FaCalendarDay,
} from "react-icons/fa";
import "../styles/BusinessDetails.css";
import formatMobileNumber from "../_utils/formatMobileNumber";
import formatCurrency from "../_utils/formatCurrency";
import formatDate from "../_utils/formatDate";

import { toBoolean } from "../_utils/to.boolean";
import { toast } from "react-hot-toast";

import { get } from "../_services/invoices.service";

export default function BusinessInvoices({ tenant }) {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [invoices, setInvoices] = useState([]);

  useEffect(() => {
    if(!tenant) {
      setInvoices([]);
      return;
    }
    const handleFetch = async () => {
      setLoading(true);
      
      try {
        const getInvoices = await get(tenant.tenantId);
        setInvoices(getInvoices.data);
      } catch (error) {
        toast.error(error.message || "Unknown error");
        setInvoices([]);
      } finally {
        setLoading(false);
      }
    }

    handleFetch();
  }, []);

  if (!tenant && !loading) {
    return (
      <div className="container py-5">
        <div className="alert alert-warning">
          Business details not available.
        </div>
      </div>
    );
  }

  const initials = tenant.businessName
    ?.split(" ")
    .map((x) => x[0])
    .join("")
    .substring(0, 2)
    .toUpperCase();

  const active = tenant.subscriptions.find(
    sub => sub.isActive === 1
  );

  const pending = invoices.find(
    inv => inv.status === "Pending"
  );

  const history = invoices.filter(
    inv => inv.status === "Pending"
  );
  
  return (
    <div className="business-details-page">

      <div className="business-header shadow">
        <div className="business-avatar">
          {initials}
        </div>

        <div className="business-header-info">
          <h2>{tenant.businessName}</h2>
          <span className="solution-badge">
            {
              active
              ? `${active.productName} - ${active.planName}`
              : "No active Subscription"
            }
          </span>
        </div>
      </div>
      
      {
        !loading 
        ? 
          <>
            <div className="row g-4 mt-1">

              <div className="col-lg-6">
                <div className="business-card">

                  <div className="card-title">
                    Pending Invoice 
                  </div>
                  {
                    pending
                    ? 
                      <>
                      {
                        new Date(pending.dueDate) <= new Date()
                        ? 
                          <BusinessItem
                            textColor='text-danger'
                            icon={<FaCalendarMinus />}
                            label="Overdue"
                            value={formatDate(pending.dueDate, "long")}
                          />
                        : 
                          <BusinessItem
                            icon={<FaCalendarDay />}
                            label="Due Date"
                            value={formatDate(pending.dueDate, "long")}
                          />
                      }
                        
                        <BusinessItem
                          icon={<FaSortNumericUp />}
                          label="Invoice Number"
                          value={pending.invoiceNumber}
                        />

                        <BusinessItem
                          icon={<FaBriefcase />}
                          label="Product Name"
                          value={`${pending.productName} - ${pending.planName}`}
                        />

                        <BusinessItem
                          icon={<FaInfoCircle />}
                          label="Details"
                          value={pending.description}
                        />

                        <BusinessItem
                          icon={<FaMoneyBillWaveAlt />}
                          label="Amount Due"
                          value={formatCurrency(pending.amount, pending.currency)}
                        />

                        <button
                          className="btn btn-primary m-2"
                          onClick={() => navigate(
                            `/chats`
                          )}
                        >
                          Submit PoP
                        </button>

                      </>
                    : 
                      <div>
                        <i className="bi bi-thumb"></i>
                        All Good here, You do not have any pending invoices
                      </div>
                  }
                </div>
              </div>

              <div className="col-lg-6">
                <div className="business-card">

                  <div className="card-title">
                    Invoice History
                  </div>

                  {
                    history.length
                    ? 
                      history.map(invoice => (
                        <BusinessItem
                          icon={<FaSortNumericUp />}
                          label={`INV${invoice.invoiceNumber} (${invoice.status})`}
                          value={`
                            ${invoice.description} 
                            Amount Due: ${formatCurrency(invoice.amount, invoice.currency)}
                            Invoice Date: ${formatDate(invoice.createdAt, "long")}
                          `}
                        />
                      ))
                    : 
                      <div>
                        <i className="bi bi-thumb"></i>
                        You do not have any passed invoices
                      </div>
                  }
                </div>
              </div>

            </div>
          </>
        :
          <div>
            <div className="spinner"></div>
            <p className="text-center">
              Loading products. <br/>
              Please wait...
            </p>
          </div>
      }

    </div>
  );
}

function BusinessItem({ icon, label, value, textColor}) {
  return (
    <div className={`business-item ${" "} ${textColor}`}>
      <div className={`business-icon ${" "} ${textColor}`}>
        {icon}
      </div>

      <div className="business-content">
        <small className={textColor}>{label}</small>
        <h6 className={textColor}>{value || "-"}</h6>
      </div>
    </div>
  );
}